import * as Database from "utils/database.utils";
import queryString from "query-string";

// Types
import { FACTOR_TO_VOTE_TYPE_MAP, VoteTypes } from "types/VoteTypes";

// Logger
import Logger from "utils/logger.utils";
const Log = new Logger();
Log.init("bqmq.utils");

export const getUserAnswerHistory = async (
    userID: string,
    sessionID: string,
    setDates: Function,
    setMyVotes: Function
) => {
    const sessionId = queryString.parse(window.location.search).session_id;

    // Check storage first for a voting session that has already started but was not submitted
    if (localStorage.getItem(`myVotes ${userID} ${sessionId}`)) {
        const existingVotes = localStorage.getItem(
            `myVotes ${userID} ${sessionId}`
        );
        if (existingVotes) {
            setMyVotes(JSON.parse(existingVotes));
            return;
        }
    }
    const result = await Database.get(
        `api/bqmq_voting/company/latest-user-votes/${sessionID}/${userID}`
    );
    const data = await result.json();
    const existingVotes: Record<string, string> = {};
    let latestDate: any = null;
    data.data.forEach((vote: Vote) => {
        const date = vote.historic_date ? vote.historic_date : vote.created;
        if (latestDate === null) latestDate = new Date(vote.created);
        const dateObj = new Date(date);
        latestDate = new Date(
            Math.max(dateObj.getTime(), latestDate.getTime())
        );
        if (vote.choice.text === "Comment") {
            existingVotes[vote.question_id] = vote.comment;
        } else {
            existingVotes[vote.question_id] = vote.choice.text;
        }
    });
    setDates(latestDate);
    setMyVotes(existingVotes);
};

export const getSessionUserComments = async (
    userId: string,
    sessionI: string,
    setInitialComments: Function,
    setComments: Function
) => {
    try {
        const result = await Database.get(
            `api/bqmq_voting/company/latest-user-comments/${sessionI}/${userId}`
        );
        const data = await result.json();

        const commentsMap: Record<string, SessionComment[]> = {};
        data.data.forEach((comment: SessionComment) => {
            if (!commentsMap[comment.factor.id]) {
                commentsMap[comment.factor.id] = [];
            }
            commentsMap[comment.factor.id].push(comment);
        });

        setInitialComments(commentsMap);
        setComments(commentsMap);
    } catch (error) {
        console.warn("Failed to load comments from API:", error);
    }
};

const getLatestCompanyDetails = async (
    companyID: string,
    sessionID: string
) => {
    const res = await Database.get(
        `api/bqmq_voting/company/latest-mode/${companyID}/${sessionID}`
    );
    if (!res.ok) return null;
    const json = await res.json();
    return json.data;
};

export const getAllFactors = async () => {
    Log.info("getAllFactors()");

    let res = await Database.get(
        "api/bqmq_voting/factors?page=1&pageSize=1000&sortKey=factor_id&sortOrder=ASC"
    );
    let data = await res.json();
    let factorsList: FactorListType = {};
    const factorMap = {
        BQ: {
            name: "Business Quality",
            key: "BQ" as FactorShortName,
            order: 1,
        },
        MQ: {
            name: "Management Quality",
            key: "MQ" as FactorShortName,
            order: 2,
        },
        FADE: {
            name: "Extended Fade",
            key: "FADE" as FactorShortName,
            order: 3,
        },
    };
    data.data.forEach((factor: BQMQFactor) => {
        const mappedFactor = factorMap[factor.text as keyof typeof factorMap];
        if (!mappedFactor) return;
        factorsList[mappedFactor.key as keyof typeof factorsList] = {
            name: mappedFactor.name,
            short_name: mappedFactor.key,
            id: factor.id,
            order: mappedFactor.order,
        };
    });

    Log.info("FACTORS: ", factorsList);

    return factorsList;
};

export const findFactorById = (factors: FactorListType, id: string) => {
    for (const [, value] of Object.entries(factors)) {
        if (id === value.id) {
            return value;
        }
    }

    return null;
};

// Vote type is parsed from factor, because it's the same thing, just named differently
export const getVoteType = (
    allFactorsList: FactorListType,
    factorId?: string | null
): string => {
    if (!factorId) {
        return VoteTypes.FULL_VOTE;
    } else {
        const factor = findFactorById(allFactorsList, factorId);

        if (factor) {
            return FACTOR_TO_VOTE_TYPE_MAP[factor.short_name];
        } else {
            return "Unknown";
        }
    }
};

export const getAnalysts = async (
    setAnalysts: Function,
    entraFilter: string = "entra"
) => {
    const response = await Database.get(
        `api/bqmq_voting/dropdowns/analysts?entra_filter=${entraFilter}`
    );
    const analysts = await response.json();
    try {
        setAnalysts(analysts.data);
    } catch (e) {
        console.error(e);
    }
};

export const getTeams = async (setTeams: Function) => {
    const response = await Database.get(`api/bqmq_voting/dropdowns/sectors`);
    const teams = await response.json();
    try {
        setTeams(teams.data);
    } catch (e) {
        console.error(e);
    }
};

export const getCompanies = async (setCompanies: Function) => {
    const response = await Database.get(`api/bqmq_voting/dropdowns/companies`);
    const companies = await response.json();
    try {
        setCompanies(companies.data);
    } catch (e) {
        console.error(e);
    }
};

export const getQuestionsForSession = async (sessionDate: string) => {
    Log.info(`getQuestionsForSession(${sessionDate})`);

    const res = await Database.get(
        `api/bqmq_voting/questions?session_date=${sessionDate}&page=1&pageSize=1000&sortKey=order_number&sortOrder=ASC`
    );
    const data = await res.json();

    return data.data;
};

export const getSessions = async (
    setSessions: Function,
    setPendingSessions: Function,
    setUpcomingSessions: Function,
    user_id: string
) => {
    const response = await Database.get(
        `api/bqmq_voting/dropdowns/sessions/${user_id}`
    );
    const sessions = await response.json();
    try {
        setSessions(sessions.data.sessions);
        setUpcomingSessions(sessions.data.upcoming_sessions);
        setPendingSessions(sessions.data.pending_sessions);
    } catch (e) {
        console.error(e);
    }
};

export const isOverallQuestion = (key: string): boolean =>
    ["bq_overall", "mq_overall", "fade"].includes(key);

export const commentSorter = (
    detailedCommentA: BQMQCommentDetail,
    detailedCommentB: BQMQCommentDetail
) => {
    if (
        detailedCommentA.text.endsWith("_comments") &&
        !detailedCommentB.text.endsWith("_comments")
    ) {
        return 1;
    } else if (
        !detailedCommentA.text.endsWith("_comments") &&
        detailedCommentB.text.endsWith("_comments")
    ) {
        return -1;
    } else {
        return 0;
    }
};

export const createPartial = (
    func: (...args: any[]) => any,
    ...args: any[]
) => {
    return function (this: any, ...innerArgs: any[]) {
        var allArguments = args.concat(innerArgs);
        return func.apply(this, allArguments);
    };
};

export const filterResults = (
    query: QueryString,
    teams: Sector[],
    data: any
) => {
    if (data.length === 0) return data;
    let newFilteredData = [...data];
    const getCompany = (session: BQMQSession) => session.company;
    const getSession = (session: BQMQSession) => session;

    let getFunc: Function;
    if (data[0].company) {
        getFunc = getCompany;
    } else {
        getFunc = getSession;
    }

    if (query.filter_search) {
        newFilteredData = newFilteredData.filter((session: any) => {
            const name = getFunc(session).name || getFunc(session).label;
            return name
                .toUpperCase()
                .includes(query.filter_search?.toUpperCase());
        });
    }

    if (query.filter_company_id) {
        newFilteredData = newFilteredData.filter((session: any) => {
            const id = getFunc(session).id || getFunc(session).value;
            return id === query.filter_company_id;
        });
    }

    if (query.filter_gim_subteam) {
        let sector = teams.filter(
            (team: Sector) =>
                team.value === query.filter_gim_subteam?.replace("_AND_", " & ")
        );
        if (sector.length === 0) return newFilteredData;
        const sectorText = sector[0].label;
        newFilteredData = newFilteredData.filter((session: any) => {
            return getFunc(session).sector?.name === sectorText;
        });
    }

    return newFilteredData;
};

export const getSelectedID = (
    filterQuery: string,
    setSelected: Function,
    includeText: string
) => {
    let companyID: string;
    if (filterQuery === "") setSelected("");
    if (filterQuery.includes(includeText)) {
        companyID = filterQuery.split(includeText)[1];
        setSelected(companyID);
    }
};

export const filterCompaniesWithNoVotes = (companies: BQMQCompany[]) => {
    return companies.filter(
        (company: BQMQCompany) =>
            company.session_count && company.session_count > 0
    );
};

export const addTableId = (sessions: BQMQSession[]): BQMQSession[] => {
    return sessions.map((row: BQMQSession, index: number) => ({
        ...row,
        tableID: row.id + row.date + index,
    }));
};

const setResult = (countedVotes: CountedVotes) => {
    const result: any = {
        BQ: {},
        MQ: {},
        FADE: {},
    };

    for (const [question, new_votes] of Object.entries(countedVotes)) {
        const typedNewVotes = new_votes as { factor: string; mode: string };
        const factorText = typedNewVotes.factor as keyof typeof result;
        result[factorText] = result[factorText] || {};
        result[factorText][question] = new_votes;
    }
    return result;
};

export const countVotes = (
    votes: Vote[],
    defaultChoices: Results
): CountedVotes => {
    const sortedVotes = [...votes].sort(
        (a, b) =>
            (a.question.order ?? Infinity) - (b.question.order ?? Infinity)
    );
    let countedVotes: CountedVotes = {};
    countedVotes = countVotesInitial(sortedVotes, defaultChoices, countedVotes);
    countedVotes = calculateMean(countedVotes);
    countedVotes = calculateFadeMode(countedVotes);
    return countedVotes;
};

const countVotesInitial = (
    votes: Vote[],
    defaultChoices: Results,
    countedVotes: CountedVotes
): CountedVotes => {
    for (const vote of votes) {
        if (vote.choice.text === "Comment") {
            continue;
        }

        if (!countedVotes[vote.question.text]) {
            countedVotes[vote.question.text] = {
                ...defaultChoices[vote.question.text as keyof Results],
                sum: 0,
                count: 0,
                mode: "",
                mean: "",
            };
        }
        countedVotes[vote.question.text][vote.choice.text] =
            (countedVotes[vote.question.text][vote.choice.text] || 0) + 1;

        countedVotes[vote.question.text]["factor"] = vote.question.factor.text;
        const numericChoice = +vote.choice.text;

        if (!isNaN(numericChoice)) {
            countedVotes[vote.question.text]["sum"] += numericChoice;
            countedVotes[vote.question.text]["count"] += 1;
        }
    }
    return countedVotes;
};

const calculateMean = (countedVotes: CountedVotes): CountedVotes => {
    for (const questionText in countedVotes) {
        const questionData = countedVotes[questionText];
        let mean = "";
        if (questionData["count"] > 0) {
            mean = `${Math.round(questionData["sum"] / questionData["count"])}`;
        }
        countedVotes[questionText]["mean"] = mean;
    }
    return countedVotes;
};

const calculateFadeMode = (countedVotes: CountedVotes): CountedVotes => {
    if (!countedVotes?.fade) {
        return countedVotes;
    }

    const FADE_METADATA_KEYS = ["count", "factor", "mean", "mode", "sum"];

    const fade = countedVotes.fade;
    let maxCount = -1;
    let mode = "";

    for (const voteOption of Object.keys(fade)) {
        if (FADE_METADATA_KEYS.includes(voteOption)) continue;
        const count = fade[voteOption];
        if (typeof count === "number" && count > maxCount) {
            maxCount = count;
            mode = voteOption;
        }
    }

    countedVotes.fade.mode = mode;
    return countedVotes;
};

const organizeCompanyData = (
    latestCompanyData: BQMQSessionExtended,
    votes: Vote[]
) => {
    let countedVotes = countVotes(votes, latestCompanyData.default_choices);
    let result = setResult(countedVotes);
    return {
        ...latestCompanyData,
        ...result,
        comments: { ...latestCompanyData.comments },
        historical_comments: { ...latestCompanyData.historical_comments },
    };
};

const getCoreUserVotes = (results: BQMQSessionExtended): Vote[] => {
    return results.votes.filter((vote: Vote) =>
        results.core
            .map((analyst: Analyst) => analyst.id)
            .includes(vote.analyst_id)
    );
};

const getCoreComments = (factor: string, results: any) => {
    return results.comments[factor].filter((comment: UserComment) =>
        results.core.map((obj: any) => obj.name).includes(comment.analyst_name)
    );
};

const getCoreHistoricalComments = (factor: string, results: any) => {
    if (!results.historical_comments) return [];
    return results.historical_comments[factor].filter((comment: UserComment) =>
        results.core.map((obj: any) => obj.name).includes(comment.analyst_name)
    );
};

const filterCoreUsersComments = (coreCompanyData: any) => {
    for (const factor of Object.keys(coreCompanyData.comments)) {
        coreCompanyData.comments[factor] = getCoreComments(
            factor,
            coreCompanyData
        );
        if (coreCompanyData.historical_comments) {
            coreCompanyData.historical_comments[factor] =
                getCoreHistoricalComments(factor, coreCompanyData);
        }
    }
};

export const getResults = async (
    companyID: string,
    sessionID: string,
    setDefaultChoices: Function,
    setSessionAllResults: Function,
    setSessionCoreResults: Function,
    onError?: () => void
) => {
    try {
        const allCompanyData = await getLatestCompanyDetails(
            companyID,
            sessionID
        );
        if (!allCompanyData) {
            onError?.();
            return;
        }
        setDefaultChoices(allCompanyData.default_choices);
        const sessionResults = organizeCompanyData(
            allCompanyData,
            allCompanyData?.votes
        );
        setSessionAllResults(sessionResults);
        const coreCompanyData = { ...allCompanyData };
        coreCompanyData.votes = getCoreUserVotes(coreCompanyData);
        const coreCompanyVotesWithComments = organizeCompanyData(
            coreCompanyData,
            coreCompanyData.votes
        );
        filterCoreUsersComments(coreCompanyVotesWithComments);
        setSessionCoreResults(coreCompanyVotesWithComments);
    } catch {
        onError?.();
    }
};

export const getFactorOverallResult = (factor: string, results: any) => {
    if (factor === "BQ") {
        return results?.session?.bq;
    } else if (factor === "MQ") {
        return results?.session?.mq;
    } else {
        // Other factors do not display overall results
        return "";
    }
};

export const isCoreResults = (coreResults: BQMQSessionExtended) => {
    return coreResults?.votes.length > 0;
};

export const hasSetupChanged = (
    originalState: BQMQSession,
    currentState: BQMQSession
) => {
    return !!(
        currentState.company &&
        JSON.stringify(originalState) !== JSON.stringify(currentState)
    );
};

export const factors = [
    {
        name: "BQ",
    },
    {
        name: "MQ",
    },
    {
        name: "FADE",
    },
];

export const COMMENTS_NAME_MAP: Record<string, string> = {
    BQ: "BQ Comments",
    MQ: "MQ Comments",
    FADE: "Fade Comments",
};

export const allFactorsList = [
    {
        name: "Business Quality",
        order: 1,
        short_name: "BQ",
    },
    {
        name: "Management Quality",
        order: 2,
        short_name: "MQ",
    },
    {
        name: "Extended Fade",
        order: 3,
        short_name: "FADE",
    },
];

export const getAllCompanySessions = async (
    setCompanySessions: Function,
    companyID: string
) => {
    try {
        let res = await Database.get(
            `api/bqmq_voting/sessions/company-active/${companyID}`
        );
        if (res.ok) {
            let response = await res.json();
            response.total = response.data.length;
            setCompanySessions(response);
        } else {
            setCompanySessions({ data: [] });
        }
    } catch (error) {
        console.error("Error fetching company sessions:", error);
        setCompanySessions({ data: [] });
    }
};

export const checkForRecentCompanySessions = async (companyId: string) => {
    try {
        const response = await Database.get(
            `api/bqmq_voting/sessions/company/${companyId}`
        );

        if (response.ok) {
            const data = await response.json();
            const companySessions = data.data || [];

            if (companySessions.length > 0) {
                // Calculate 1.5 months ago (45 days)
                const oneAndHalfMonthsAgo = new Date();
                oneAndHalfMonthsAgo.setDate(oneAndHalfMonthsAgo.getDate() - 45);

                // Find the latest session within the last 1.5 months
                const recentSessions = companySessions.filter(
                    (session: any) => {
                        // Parse session date (could be start_date, created_date, or date field)
                        const sessionDate = new Date(
                            session.start_date ||
                                session.created_date ||
                                session.date
                        );
                        return sessionDate >= oneAndHalfMonthsAgo;
                    }
                );

                if (recentSessions.length > 0) {
                    // Sort by date descending to get the latest session
                    recentSessions.sort((a: any, b: any) => {
                        const dateA = new Date(
                            a.start_date || a.created_date || a.date
                        );
                        const dateB = new Date(
                            b.start_date || b.created_date || b.date
                        );
                        return dateB.getTime() - dateA.getTime();
                    });

                    return recentSessions[0]; // Return the latest session
                }
            }
        }
    } catch (error) {
        console.error("Error checking for recent sessions:", error);
    }
    return null; // No recent session found
};

export const percentageFormatter = (value: number, price: number): string => {
    const percentageChange = (value / price - 1) * 100;
    const roundedPercentage = Math.round(percentageChange);
    const percentageToString = roundedPercentage.toString();
    if (
        isNaN(Number(percentageToString)) ||
        percentageToString.toLowerCase() === "infinity"
    ) {
        return "N/A";
    }
    return percentageToString + "%";
};

export const getCommentHistory = async (
    commentId: string
): Promise<SessionComment[]> => {
    try {
        const result = await Database.get(
            `api/bqmq_voting/comments/${commentId}/history`
        );
        const data = await result.json();
        return data.data || [];
    } catch (error) {
        console.warn("Failed to load comment history from API:", error);
        return [];
    }
};
